import { ReactNode, useLayoutEffect, useRef } from 'react';

import { cn } from 'src/lib/utils';

import { Dialog, DialogContent } from 'src/components/ui/dialog';

export interface CustomModalProps {
  show: boolean;
  onHide?: () => void;
  children: ReactNode;
  size?: 'lg' | 'xl';
  fullscreen?: 'xl-down';
  scrollable?: boolean;
  closeButton?: boolean;
  keyboard?: boolean;
  backdrop?: 'static' | false;
  overlayClassName?: string;
  'data-test'?: string;
}

const SIZE_CLASSES: Record<NonNullable<CustomModalProps['size']>, string> = {
  lg: 'tw:sm:max-w-3xl',
  xl: 'tw:sm:max-w-6xl',
};

const FULLSCREEN_DESKTOP_WIDTH: Record<
  NonNullable<CustomModalProps['size']> | 'default',
  string
> = {
  default: 'tw:xl:max-w-lg',
  lg: 'tw:xl:max-w-3xl',
  xl: 'tw:xl:max-w-6xl',
};

const FULLSCREEN_XL_DOWN =
  'tw:my-0 tw:h-dvh tw:max-h-dvh tw:w-screen tw:max-w-none tw:sm:max-w-none tw:rounded-none tw:border-0 tw:xl:my-4 tw:xl:h-auto tw:xl:max-h-[calc(100dvh-2rem)] tw:xl:w-full tw:xl:rounded-lg tw:xl:border';

export default function CustomModal({
  show,
  onHide,
  children,
  size,
  fullscreen,
  scrollable = false,
  closeButton = false,
  keyboard = false,
  backdrop = 'static',
  overlayClassName,
  'data-test': dataTest,
}: CustomModalProps) {
  // Radix restores focus to its Trigger; these modals open from state, so
  // remember the opener instead.
  const returnFocusTo = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (show && document.activeElement instanceof HTMLElement) {
      returnFocusTo.current = document.activeElement;
    }
  }, [show]);

  return (
    <Dialog
      open={show}
      onOpenChange={(open) => {
        if (!open) onHide?.();
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        data-test={dataTest}
        showCloseButton={closeButton}
        overlayClassName={cn(
          backdrop === false && 'tw:bg-transparent',
          overlayClassName,
        )}
        className={cn(
          'tw:gap-0 tw:p-0',
          size && SIZE_CLASSES[size],
          scrollable &&
            'tw:flex tw:max-h-[calc(100dvh-2rem)] tw:flex-col tw:overflow-hidden tw:[&_[data-slot=dialog-body]]:overflow-y-auto',
          fullscreen === 'xl-down' && [
            FULLSCREEN_XL_DOWN,
            FULLSCREEN_DESKTOP_WIDTH[size ?? 'default'],
          ],
        )}
        onEscapeKeyDown={(event) => {
          if (!keyboard) event.preventDefault();
        }}
        onOpenAutoFocus={(event) => {
          // Focusing the first field would open popups such as a datepicker.
          event.preventDefault();
          (event.currentTarget as HTMLElement).focus();
        }}
        onInteractOutside={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => {
          const target = returnFocusTo.current;
          if (target?.isConnected) {
            event.preventDefault();
            target.focus();
          }
        }}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}
